import React, { useMemo, useState } from 'react';
import { normalizeGalleryImages } from '../../utils/imageLightbox';
import ImageLightboxModal from './ImageLightboxModal';

/**
 * Thumbnail grid that opens a full-size lightbox on click (detail pages).
 */
const ClickableImageGrid = ({
  images = [],
  title = 'Images',
  namePrefix = 'Image',
  className = '',
  gridClassName = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
  imageClassName = 'w-full h-48 object-cover rounded-lg',
  showCard = true,
  renderBadge,
}) => {
  const slides = useMemo(
    () => normalizeGalleryImages(images, namePrefix),
    [images, namePrefix],
  );
  const [openIndex, setOpenIndex] = useState(null);

  if (slides.length === 0) return null;

  const grid = (
    <div className={gridClassName}>
      {slides.map((slide, index) => (
        <button
          key={slide.id}
          type="button"
          onClick={() => setOpenIndex(index)}
          className="group relative block w-full text-left rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2"
          aria-label={`View ${slide.alt}`}
        >
          <img
            src={slide.url}
            alt={slide.alt}
            className={`${imageClassName} group-hover:opacity-95 transition-opacity`}
            loading="lazy"
          />
          {typeof renderBadge === 'function' && renderBadge(images[index], index)}
          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
            <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-1 rounded">
              View full size
            </span>
          </span>
        </button>
      ))}
    </div>
  );

  const lightbox =
    openIndex !== null ? (
      <ImageLightboxModal
        isOpen
        images={slides}
        startIndex={openIndex}
        onClose={() => setOpenIndex(null)}
      />
    ) : null;

  if (!showCard) {
    return (
      <>
        {grid}
        {lightbox}
      </>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-6 mb-6 ${className}`.trim()}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      {grid}
      {lightbox}
    </div>
  );
};

export default ClickableImageGrid;
